import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Checkbox, IconButton, Stack, Switch } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FaRegEdit } from 'react-icons/fa';
import { Field, getAllClosureCustomFields, updateClosureCustomField } from 'wisestep-api';
import { WsColors, WsDataTable, WsDialogSize } from 'wisestep-base';

import { SettingsPageLayout } from '../../../components/SettingsPageLayout';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import { useDialog } from '../../../hooks/useDialog';
import { success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { CustomFieldFormClosure } from '../../closures/components/CustomFieldFormClosure';
import {
  placementSettingsIndexOptions,
  hardcodedClosureFields,
  fieldTypes,
  FieldIconMap,
} from '../helper/ClosureHelper';

export type FieldType = typeof fieldTypes[number];

const mapDTOToField = (field: any) => ({
  ...field,
  ...(field.options && { options: field.options.map((value) => ({ value })) }),
});

const CustomizeClosureFields = () => {
  const [openDialog, closeDialog] = useDialog();
  const [fields, setFields] = useState<Field[] | null>(null);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const fetchAllClosureCustomFields = async () => {
    const { data } = await getAllClosureCustomFields();
    setFields(data.map(mapDTOToField));
  };

  useEffect(() => {
    fetchAllClosureCustomFields();
  }, []);

  const openCustomFieldForm = (defaultValues?) => {
    openDialog({
      children: (
        <CustomFieldFormClosure
          defaultValues={defaultValues}
          onClose={closeDialog}
          onSave={async () => {
            await fetchAllClosureCustomFields();
            closeDialog();
          }}
        />
      ),
      size: WsDialogSize.MEDIUM,
    });
  };

  const columns = [
    {
      Header: 'Attribute',
      accessor: 'Attribute',
      Cell: ({ row: { original: row } }) => {
        const Icon = FieldIconMap[row.type];
        return (
          <Stack direction="row" alignItems="center">
            <Icon />
            <span style={{ marginLeft: '1rem' }}>{row.label}</span>
          </Stack>
        );
      },
    },
    {
      Header: 'Visible',
      accessor: 'Visible',
      Cell: ({ row: { original: row } }) =>
        !row.hardcoded && (
          <Switch
            defaultChecked={row.visible}
            onChange={async (_event, checked) => {
              await updateClosureCustomField({ ...row, visible: checked });
              setNotification(success(`Custom Field saved successfully!`));
              await fetchAllClosureCustomFields();
            }}
          />
        ),
    },
    {
      Header: 'Mandatory',
      accessor: 'Mandatory',
      Cell: ({ row: { original: row } }) =>
        !row.hardcoded && (
          <Checkbox
            defaultChecked={row.required}
            onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
              await updateClosureCustomField({ ...row, required: event.target.checked });
              setNotification(success(`Custom Field saved successfully!`));
              await fetchAllClosureCustomFields();
            }}
          />
        ),
    },
    {
      Header: 'Contract',
      accessor: 'Contract',
      Cell: ({ row: { original: row } }) =>
        !row.hardcoded && (
          <Checkbox
            defaultChecked={row.contract}
            onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
              await updateClosureCustomField({ ...row, required: event.target.checked });
              setNotification(success(`Custom Field saved successfully!`));
              await fetchAllClosureCustomFields();
            }}
          />
        ),
    },
    {
      Header: 'Permanent',
      accessor: 'Permanent',
      Cell: ({ row: { original: row } }) =>
        !row.hardcoded && (
          <Checkbox
            defaultChecked={row.permanent}
            onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
              await updateClosureCustomField({ ...row, required: event.target.checked });
              setNotification(success(`Custom Field saved successfully!`));
              await fetchAllClosureCustomFields();
            }}
          />
        ),
    },
    {
      Header: '',
      accessor: 'accessor',
      Cell: ({ row: { original: row } }) =>
        !row.hardcoded && (
          <IconButton sx={{ color: WsColors.TheBlues500 }}>
            <FaRegEdit onClick={() => openCustomFieldForm(row)} />
          </IconButton>
        ),
    },
  ];

  return (
    <SettingsPageLayout
      title="Customize Placement Fields"
      indexOptions={placementSettingsIndexOptions}
      breadCrumnbsList={getSettingBreadCrumbList(
        ISettingBreadCrumbTypes.PLACEMENTS,
        Pages['CUSTOMIZE_PLACEMENT_FIELDS']
      )}
    >
      {fields && (
        <Box display="flex" flexDirection="column" alignItems="flex-end" px={2}>
          <Box my={1}>
            <Button onClick={() => openCustomFieldForm()} startIcon={<AddIcon />} disableElevation>
              Add Field
            </Button>
          </Box>
          <WsDataTable
            elevated={false}
            dense
            columns={columns}
            data={[...hardcodedClosureFields, ...fields] as []}
          />
        </Box>
      )}
    </SettingsPageLayout>
  );
};

export default CustomizeClosureFields;
